import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { DailyHistory, withPreviousClose } from '../../../../../models/daily-history.model';
import { DailyHistoryService } from '../../../../../services/daily-history.service';

/** The endpoint's own default, and what the tab asks for a page at a time. */
export const TRADE_HISTORY_PAGE_SIZE = 20;

export interface TradeHistoryStoreState {
	history: DailyHistory[];
	totalCount: number;
	pageNumber: number;
	contractId: number | null;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	failedPageNumber: number | null;
	endReached: boolean;
}

const initialState: TradeHistoryStoreState = {
	history: [],
	totalCount: 0,
	pageNumber: 0,
	contractId: null,
	loading: false,
	loadingMore: false,
	error: null,
	failedPageNumber: null,
	endReached: false
};

export const TradeHistoryStore = signalStore(
	withState(initialState),
	withComputed(({ history, loading, loadingMore, error, endReached }) => ({
		/** Each day with the close it is measured against, recomputed as pages arrive. */
		days: computed(() => withPreviousClose(history())),
		isEmpty: computed(() => !loading() && !loadingMore() && history().length === 0),
		canLoadMore: computed(() => !endReached() && !loading() && !loadingMore() && !error())
	})),
	withMethods((store, dailyHistoryService = inject(DailyHistoryService)) => {
		let requestGeneration = 0;

		async function loadPage(pageNumber: number, requestedContractId?: number): Promise<void> {
			const generation = ++requestGeneration;
			const isFirstPage = pageNumber === 1;
			const contractId = (isFirstPage ? requestedContractId : null) ?? store.contractId();

			if (!contractId) {
				return;
			}

			patchState(store, {
				error: null,
				failedPageNumber: null,
				contractId,
				...(isFirstPage ? { loading: true, endReached: false } : { loadingMore: true })
			});

			try {
				const data = await firstValueFrom(
					dailyHistoryService.getDailyHistory({
						contractId,
						pageNumber,
						pageSize: TRADE_HISTORY_PAGE_SIZE
					})
				);
				if (generation !== requestGeneration) {
					return;
				}

				const held = isFirstPage ? [] : store.history();
				const heldDays = new Set(held.map(day => day.timeOfEvent));
				const fresh = data.items.filter(day => !heldDays.has(day.timeOfEvent));
				const history = [...held, ...fresh];

				patchState(store, {
					history,
					totalCount: data.totalCount,
					pageNumber,
					loading: false,
					loadingMore: false,
					endReached:
						fresh.length === 0 ||
						data.items.length < TRADE_HISTORY_PAGE_SIZE ||
						history.length >= data.totalCount
				});
			} catch {
				if (generation !== requestGeneration) {
					return;
				}

				patchState(store, {
					error: 'دریافت سابقه معاملات با خطا مواجه شد',
					failedPageNumber: pageNumber,
					loading: false,
					loadingMore: false
				});
			}
		}

		return {
			loadFirstPage(contractId: number): Promise<void> {
				return loadPage(1, contractId);
			},

			loadNextPage(): Promise<void> {
				if (!store.canLoadMore()) {
					return Promise.resolve();
				}
				return loadPage(store.pageNumber() + 1);
			},

			retry(): Promise<void> {
				return loadPage(store.failedPageNumber() ?? 1);
			}
		};
	})
);
